import { eventLayoutCopyIssue, insertEventLayout, insertHomeVariation } from './experienceModel'

export const isLayoutClipboard = (kind) => ['event_layout', 'home_variation'].includes(kind)

// Schema validates the shape; these are the registered relationships enforced
// by the publication validator, checked before copied content enters a draft.
export function validateLayoutClipboard(kind, content) {
  if (!content.name.trim()) throw new Error('Name the page before copying it.')
  if (kind === 'home_variation') return
  if (content.scope.kind !== 'default' && !content.scope.value.trim()) throw new Error('Choose the page’s sport, league or game before copying it.')
  const identities = [content.id, content.screen_id, ...Object.values(content.scenes ?? {}).map((scene) => scene.id)]
  if (new Set(identities).size !== identities.length) throw new Error('Copied page has repeated identities. Correct the source before copying.')
  const sectionIDs = new Map(), slots = new Map()
  for (const layout of [content.layout, ...Object.values(content.scenes ?? {}).map((scene) => scene.layout)]) {
    if (!layout.sections) continue
    const kinds = new Set(), ids = new Set(), layoutSlots = new Set()
    for (const [index, section] of layout.sections.entries()) {
      const { id, slot_id: slot, type, enabled } = section
      if (kinds.has(type) || ids.has(id) || layoutSlots.has(slot)) throw new Error('Each event section may appear only once in a layout state.')
      kinds.add(type); ids.add(id); layoutSlots.add(slot)
      const previous = sectionIDs.get(id)
      if ((previous && (previous.type !== type || previous.slot_id !== slot)) || (slots.has(slot) && slots.get(slot) !== id)) throw new Error('An event section must keep the same identity across layout states. Correct the source before copying.')
      sectionIDs.set(id, section); slots.set(slot, id)
      if ((['event_header', 'event_markets'].includes(type) && !enabled) || (type === 'event_header' && index !== 0)) throw new Error('Keep the matchup header first and keep the header and markets visible.')
    }
    if (!kinds.has('event_header') || !kinds.has('event_markets')) throw new Error('An event layout must include the matchup header and markets.')
  }
  if ([...sectionIDs.keys()].some((id) => slots.has(id) || identities.includes(id)) || [...slots.keys()].some((id) => identities.includes(id))) throw new Error('Copied layout has conflicting section identities. Correct the source before copying.')
}

export function layoutPasteIssue(document, selection, payload, destination = {}) {
  if (!document.experience) return 'Open Event pages or Home variations before pasting a layout.'
  if (payload.kind === 'event_layout') {
    if (selection.surface !== 'events') return 'Open Event pages to paste this layout.'
    return eventLayoutCopyIssue(document.experience, destination.scope ?? {})
  }
  if (selection.surface !== 'scenes') return 'Open Home variations to paste this variation.'
  if (document.experience.home_scenes.length >= 16) return 'This Home has reached its variation limit.'
  const components = new Map(document.screens[0].components.map((item) => [item.id, item]))
  const refs = new Set([...payload.content.component_order, ...payload.content.hidden_component_ids])
  const missing = [...refs].filter((id) => !components.has(id)).length
  if (missing) return `This variation uses ${missing} Home ${missing === 1 ? 'section that is' : 'sections that are'} missing here. Restore the missing Home content or paste into a revision with the same sections.`
  if ([...refs].some((id) => components.get(id).type === 'compliance_notice')) return 'A Home variation cannot reorder or hide required notices.'
  return null
}

export function pasteNativeLayout(document, selection, payload, destination, uuid) {
  const result = payload.kind === 'event_layout'
    ? insertEventLayout(document.experience, payload.content, destination.scope, destination.name || payload.content.name, uuid)
    : insertHomeVariation(document.experience, payload.content, selection.sceneId, uuid)
  return {
    document: { ...document, experience: result.experience }, kind: payload.kind,
    name: (result.template ?? result.scene).name,
    selection: result.template ? { surface: 'events', templateId: result.template.id, eventId: null, layer: 'base' } : { surface: 'scenes', sceneId: result.scene.id },
  }
}
